import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Ban, BookCheck, Loader2, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  PAYMENT_STATUS_BADGE_VARIANT,
  PURCHASE_STATUS_BADGE_VARIANT,
} from "@/components/purchases/format";
import { PurchaseInvoiceForm } from "@/components/purchases/PurchaseInvoiceForm";
import { translatePurchaseError } from "@/components/purchases/error-translations";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import {
  useDiscardPurchaseInvoice,
  usePostPurchaseInvoice,
  usePurchaseInvoice,
  useVoidPurchaseInvoice,
} from "@/lib/api/purchase-invoices.queries";
import { useTeamPermission } from "@/lib/team-context";
import { usePaymentVouchers } from "@/lib/api/payment-vouchers.queries";
import { ApiError } from "@/lib/api";
import { useActiveCompany } from "@/lib/company-context";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import { DocumentAttachments } from "@/components/documents/DocumentAttachments";

export const Route = createFileRoute(
  "/companies/$companyId/compras/$invoiceId",
)({
  component: PurchaseInvoiceDetailPage,
});

function PurchaseInvoiceDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <PurchaseInvoiceDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function PurchaseInvoiceDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, invoiceId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const detailQuery = usePurchaseInvoice(companyId, invoiceId);
  const postMutation = usePostPurchaseInvoice(companyId);
  const voidMutation = useVoidPurchaseInvoice(companyId);
  const discardMutation = useDiscardPurchaseInvoice(companyId);
  const canWrite = useTeamPermission("purchases.write");
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  // Payments applied against this invoice (egresos del proveedor, filtered
  // client-side to the applications touching this invoice).
  const vouchersQuery = usePaymentVouchers(
    companyId,
    detailQuery.data?.status !== "draft" && detailQuery.data
      ? { terceroId: detailQuery.data.supplierTerceroId, pageSize: 100 }
      : { pageSize: 1 },
  );

  if (detailQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <ErrorState
        title={t("purchases.detail.error_title")}
        description={t("purchases.detail.error_body")}
        error={detailQuery.error ?? undefined}
        onRetry={() => void detailQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const invoice = detailQuery.data;
  const relatedVouchers = (vouchersQuery.data?.vouchers ?? []).filter(
    (v) => v.status !== "void",
  );

  async function handlePost() {
    try {
      // Post the revision on screen: a save by someone else since it was read is refused, not booked.
      const res = await postMutation.mutateAsync({ invoiceId, expectedRevision: invoice.revision });
      toast.success(
        t("purchases.toast.posted_entry", { entry: res.entryNumber }),
      );
      if (res.inventory && res.inventory.errors.length > 0) {
        toast.warning(
          t("purchases.toast.inventory_errors", {
            count: res.inventory.errors.length,
          }),
        );
      }
    } catch (err) {
      toast.error(translatePurchaseError(err, t));
    }
  }

  async function handleDiscard() {
    try {
      await discardMutation.mutateAsync({ invoiceId, expectedRevision: invoice.revision });
      toast.success(t("purchases.detail.discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/compras", params: { companyId } });
    } catch (err) {
      toast.error(translatePurchaseError(err, t));
      setDiscardOpen(false);
    }
  }

  async function handleVoid() {
    try {
      await voidMutation.mutateAsync({
        invoiceId,
        reason: voidReason.trim() || undefined,
      });
      toast.success(t("purchases.toast.voided"));
      setVoidOpen(false);
      setVoidReason("");
    } catch (err) {
      if (err instanceof ApiError && err.code === "HAS_PAYMENTS") {
        toast.error(t("purchases.error.HAS_PAYMENTS"));
      } else {
        toast.error(translatePurchaseError(err, t));
      }
    }
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/compras"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("purchases.detail.back")}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              {invoice.invoiceNumber}
            </h1>
            <Badge variant={PURCHASE_STATUS_BADGE_VARIANT[invoice.status]}>
              {invoice.source === "import" && invoice.status === "draft"
                ? t("purchases.status.imported_draft")
                : t(`purchases.status.${invoice.status}`)}
            </Badge>
            {invoice.documentTypeCode && invoice.documentTypeCode !== "01" ? (
              <Badge variant="outline">{t("purchases.equivalente_badge")}</Badge>
            ) : null}
            {invoice.source === "import" ? (
              <Badge variant="secondary">{t("purchases.imported_tag")}</Badge>
            ) : null}
            {invoice.status === "posted" ? (
              <Badge variant={PAYMENT_STATUS_BADGE_VARIANT[invoice.paymentStatus]}>
                {t(`purchases.paymentStatus.${invoice.paymentStatus}`)}
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {invoice.supplierLegalName ?? invoice.supplierTerceroId}
            {invoice.supplierIdentificationNumber
              ? ` · NIT ${invoice.supplierIdentificationNumber}`
              : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite && invoice.editable && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("purchases.detail.edit")}
            </Button>
          ) : null}
          {canWrite && invoice.status === "draft" && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discardMutation.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("purchases.detail.discard")}
            </Button>
          ) : null}
          {invoice.status === "draft" && !editing ? (
            <Button onClick={() => void handlePost()} disabled={postMutation.isPending}>
              <BookCheck className="h-4 w-4" />
              {t("purchases.actions.post")}
            </Button>
          ) : null}
          {invoice.status === "posted" ? (
            <Button
              variant="destructive"
              onClick={() => setVoidOpen(true)}
              disabled={voidMutation.isPending}
            >
              <Ban className="h-4 w-4" />
              {t("purchases.actions.void")}
            </Button>
          ) : null}
        </div>
      </div>

      {invoice.status === "void" && invoice.voidReason ? (
        <Card className="border-destructive/40">
          <CardContent className="pt-6 text-sm">
            <span className="font-medium">{t("purchases.detail.void_reason")}:</span>{" "}
            {invoice.voidReason}
          </CardContent>
        </Card>
      ) : null}

      {editing ? (
        <PurchaseInvoiceForm
          companyId={companyId}
          initialInvoice={invoice}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      <div className={editing ? "hidden" : "grid gap-6 lg:grid-cols-[1fr_300px]"}>
        <div className="flex flex-col gap-6">
          {/* Lines */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("purchases.detail.lines")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("purchases.detail.line_description")}</TableHead>
                    <TableHead>{t("purchases.detail.line_account")}</TableHead>
                    <TableHead className="text-right">
                      {t("purchases.detail.line_quantity")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("purchases.detail.line_unit_price")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("purchases.detail.line_iva")}
                    </TableHead>
                    <TableHead className="text-right">
                      {t("purchases.detail.line_total")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>
                        <p className="text-sm">{line.description}</p>
                        {line.warehouseId ? (
                          <p className="text-[11px] text-muted-foreground">
                            {t("purchases.detail.line_warehouse")}
                          </p>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {line.accountCode ?? "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {line.quantity}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {line.ivaAmount > 0
                          ? `${formatCop(line.ivaAmount)} (${line.ivaPercent}%)`
                          : line.incAmount > 0
                            ? `INC ${formatCop(line.incAmount)}`
                            : "—"}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.lineExtensionAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Payments applied — only meaningful once posted */}
          {invoice.status !== "draft" ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("purchases.detail.payments")}
                </CardTitle>
                <CardDescription>
                  {t("purchases.detail.payments_hint", {
                    paid: formatCop(invoice.paidAmount),
                    payable: formatCop(invoice.payableAmount),
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {invoice.paidAmount <= 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("purchases.detail.payments_empty")}
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {relatedVouchers.map((voucher) => (
                      <Link
                        key={voucher.id}
                        to="/companies/$companyId/egresos/$voucherId"
                        params={{ companyId, voucherId: voucher.id }}
                        className="flex items-center justify-between rounded-md border px-3 py-2 text-sm hover:bg-accent"
                      >
                        <span className="font-mono">
                          CE-{voucher.voucherNumber}
                        </span>
                        <span className="text-muted-foreground">
                          {voucher.paymentDate}
                        </span>
                        <span className="font-mono">
                          {formatCop(voucher.totalApplied)}
                        </span>
                      </Link>
                    ))}
                    <p className="text-[11px] text-muted-foreground">
                      {t("purchases.detail.payments_note")}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          {invoice.notes ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("purchases.detail.notes")}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm">{invoice.notes}</CardContent>
            </Card>
          ) : null}
        </div>

        {/* Totals sidebar */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-base">
              {t("purchases.totals.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row
              label={t("purchases.totals.subtotal")}
              value={formatCop(invoice.lineExtensionAmount)}
            />
            {invoice.allowanceTotalAmount > 0 ? (
              <Row
                label={t("purchases.totals.allowance")}
                value={`- ${formatCop(invoice.allowanceTotalAmount)}`}
              />
            ) : null}
            {invoice.chargeTotalAmount > 0 ? (
              <Row
                label={t("purchases.totals.charge")}
                value={`+ ${formatCop(invoice.chargeTotalAmount)}`}
              />
            ) : null}
            <Row
              label={t("purchases.totals.tax_exclusive")}
              value={formatCop(invoice.taxExclusiveAmount)}
            />
            {invoice.ivaAmount > 0 ? (
              <Row
                label={t("purchases.totals.iva_descontable")}
                value={formatCop(invoice.ivaAmount)}
              />
            ) : null}
            {invoice.incAmount > 0 ? (
              <Row
                label={t("purchases.totals.inc")}
                value={formatCop(invoice.incAmount)}
              />
            ) : null}
            <Row
              label={t("purchases.totals.total")}
              value={formatCop(invoice.totalAmount)}
              bold
            />
            {invoice.retefuenteAmount > 0 ? (
              <Row
                label={t("purchases.totals.retefuente")}
                value={`- ${formatCop(invoice.retefuenteAmount)}`}
              />
            ) : null}
            {invoice.reteivaAmount > 0 ? (
              <Row
                label={t("purchases.totals.reteiva")}
                value={`- ${formatCop(invoice.reteivaAmount)}`}
              />
            ) : null}
            {invoice.reteicaAmount > 0 ? (
              <Row
                label={t("purchases.totals.reteica")}
                value={`- ${formatCop(invoice.reteicaAmount)}`}
              />
            ) : null}
            <div className="border-t pt-2">
              <Row
                label={t("purchases.totals.net_payable")}
                value={formatCop(invoice.payableAmount)}
                bold
              />
              <Row
                label={t("purchases.detail.paid")}
                value={formatCop(invoice.paidAmount)}
              />
              <Row
                label={t("purchases.detail.outstanding")}
                value={formatCop(
                  Math.max(0, invoice.payableAmount - invoice.paidAmount),
                )}
                bold
              />
            </div>

            <div className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
              <p>
                {t("purchases.detail.issue_date")}: {invoice.issueDate}
              </p>
              {invoice.dueDate ? (
                <p>
                  {t("purchases.detail.due_date")}: {invoice.dueDate}
                </p>
              ) : null}
              {invoice.cufe ? (
                <p className="break-all">CUFE: {invoice.cufe}</p>
              ) : null}
              {invoice.journalEntryId ? (
                <Link
                  to="/companies/$companyId/contabilidad/diario/$entryId"
                  params={{ companyId, entryId: invoice.journalEntryId }}
                  className="inline-block text-primary hover:underline"
                >
                  {t("purchases.detail.journal_link")}
                </Link>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Accounting traceability */}
      <Card className={editing ? "hidden" : undefined}>
        <CardHeader>
          <CardTitle className="text-base">
            {t("journal.document.title")}
          </CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="purchase_invoices"
            documentId={invoiceId}
          />
        </CardContent>
      </Card>

      {/* Soportes vinculados a la compra (la factura física del proveedor…) — PF-348 */}
      {!editing ? (
        <DocumentAttachments
          companyId={companyId}
          entityType="purchase_invoice"
          entityId={invoiceId}
        />
      ) : null}

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("purchases.detail.discard_title", { number: invoice.invoiceNumber })}
        description={t("purchases.detail.discard_body")}
        pending={discardMutation.isPending}
        onConfirm={() => void handleDiscard()}
      />

      {/* Void dialog */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("purchases.void.title")}</DialogTitle>
            <DialogDescription>{t("purchases.void.body")}</DialogDescription>
          </DialogHeader>
          {invoice.paidAmount > 0 ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
              {t("purchases.void.has_payments_warning")}
            </p>
          ) : null}
          <div>
            <Label htmlFor="purchase-void-reason">
              {t("purchases.void.reason")}
            </Label>
            <textarea
              id="purchase-void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={2}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={t("purchases.void.reason_placeholder")}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleVoid()}
              disabled={voidMutation.isPending}
            >
              {t("purchases.void.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
